import { format, isSameYear } from "date-fns"

import type { ProjectStatusTypes } from "@productlane/ui"
import type { Project } from "@productlane/zero"
import { ProjectStatusIconKeys } from "@productlane/ui"

export type ProjectPreviewInput = Pick<
	Project,
	"state" | "progress" | "targetDate"
> &
	Partial<
		Pick<
			Project,
			| "stateName"
			| "stateColor"
			| "startDate"
			| "linearTeamIds"
			| "linearTeamNames"
			| "leadId"
		>
	>

export interface ProjectPreviewDetails {
	status: ProjectStatusTypes | null
	statusLabel: string | null
	statusColor: string | null
	primaryTeamId: string | null
	primaryTeam: string | null
	extraTeamCount: number
	progressPercent: number | null
	dateLabel: string | null
}

const formatDate = (timestamp: number, now: Date) =>
	format(timestamp, isSameYear(timestamp, now) ? "MMM d" : "MMM d, yyyy")

export const getProjectPreviewDetails = (
	project: ProjectPreviewInput,
	now: Date = new Date(),
): ProjectPreviewDetails => {
	const status = ProjectStatusIconKeys.find((key) => key === project.state)
	const stateLabel = project.state
		? project.state.charAt(0).toUpperCase() + project.state.slice(1)
		: null
	const teams = (project.linearTeamNames ?? []).filter(Boolean)
	const teamIds = project.linearTeamIds ?? []
	const progress =
		typeof project.progress === "number"
			? Math.round(Math.min(Math.max(project.progress, 0), 1) * 100)
			: null

	let dateLabel: string | null = null
	if (project.startDate && project.targetDate) {
		dateLabel = `${formatDate(project.startDate, now)} → ${formatDate(project.targetDate, now)}`
	} else if (project.targetDate) {
		dateLabel = formatDate(project.targetDate, now)
	}

	return {
		status: status ?? null,
		statusLabel: project.stateName || stateLabel,
		statusColor: project.stateColor ?? null,
		primaryTeamId: teamIds[0] ?? null,
		primaryTeam: teams[0] ?? null,
		extraTeamCount: Math.max(Math.max(teams.length, teamIds.length) - 1, 0),
		progressPercent: progress,
		dateLabel,
	}
}
